import { MobileDateTimePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { Button, Label, Radio, Spinner, TextInput, Textarea } from 'flowbite-react';
import React, { useEffect } from 'react';

import CreatableSelect from 'react-select/creatable';
import { Dropdown, camelCaseToSpace, validateEmailAddress, validateUrl } from 'shared-utils';
import {
	ApplicationRoundType,
	BriedDebriefCallType,
	BriefDebriefInput,
	BriefDebriefCallDocument,
	RoundLabelType,
} from 'types';
import Select, { MultiValue, SingleValue } from 'react-select';

import GenerateMeetingLinkButton from '../shared/GenerateMeetingLinkButton';
import { useGetAllPrepCoaches } from 'src/hooks/user.hooks';

const BriefDebriefModal = ({
	onSubmit,
	isLoading,
	positionId,
	roundNumber,
	defaultScheduleType,
	callDetails,
}: {
	userId?: string;
	userName?: string;
	isLoading?: boolean;
	positionId?: string;
	onFinish?: () => void;
	onSubmit?: (data: BriefDebriefInput) => void;
	roundNumber?: number;
	defaultScheduleType?: 'request' | 'schedule';
	callDetails?: BriefDebriefCallDocument;
}) => {
	const [value, setValue] = React.useState<MultiValue<Dropdown>>([]);
	const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
	const [inputValue, setInputValue] = React.useState('');
	const [scheduleType, setScheduleType] = React.useState<'request' | 'schedule'>(defaultScheduleType || 'request');
	const [formData, setFormData] = React.useState<BriefDebriefInput>({
		callType: BriedDebriefCallType.BRIEF,
		message: '',
		additionalEmails: [],
		prepCoach: '',
		roundNumber: roundNumber || 0,
		roundType: ApplicationRoundType.Assignment,
		label: RoundLabelType.Screening,
	});

	const { data: prepCoachesData, isLoading: prepCoachesLoading } = useGetAllPrepCoaches();

	const handleInputChange = (newValue: string) => {
		if (!newValue) return;
		switch (newValue[newValue.length - 1]) {
			case ' ':
			case ',':
				// Check if inputValue already exists in the array
				if (validateEmailAddress(inputValue) && !value.some(item => item.value === inputValue)) {
					// If not, add it to the array
					setValue(prev => [...prev, { value: inputValue, label: inputValue }]);
					setFormData(prev => {
						return { ...prev, additionalEmails: [...value.map(val => val.value), inputValue] };
					});
				}
				if (!validateEmailAddress(inputValue)) setEmailErrorMessage('Please enter a valid email address');
				else setEmailErrorMessage('');
				setInputValue('');
				break;
		}
	};
	const [isFormFilled, setIsFormFilled] = React.useState(false);
	const labelStyle = `dark:text-white text-[#111928]`;
	useEffect(() => {
		// if all fields are filled then make the form filled else not
		if (
			formData.message &&
			(scheduleType === 'request' ||
				(formData.meetingLink && validateUrl(formData.meetingLink) && formData.meetingDateTime && formData.duration)) &&
			formData.callType &&
			formData.label &&
			formData.prepCoach
		) {
			setIsFormFilled(true);
		} else {
			setIsFormFilled(false);
		}
	}, [formData]);

	const prepCoachesOptions =
		prepCoachesData?.users?.map(u => ({ label: `${u.name} <${u.email}>`, value: String(u._id) })) ?? [];

	useEffect(() => {
		if (scheduleType === 'request') {
			setFormData(prev => ({ ...prev, meetingDateTime: undefined, meetingLink: undefined, duration: undefined }));
		}
	}, [scheduleType]);

	useEffect(() => {
		if (defaultScheduleType) setScheduleType(defaultScheduleType);
	}, [defaultScheduleType]);

	useEffect(() => {
		if (callDetails) {
			setFormData({
				callType: callDetails.callType,
				message: callDetails.message,
				additionalEmails: callDetails.additionalEmails,
				prepCoach: callDetails.prepCoach?._id,
				roundNumber: callDetails.roundNumber,
				roundType: callDetails.roundType,
				label: callDetails.label,
				meetingDateTime: callDetails.meetingDateTime,
				meetingLink: callDetails.meetingLink,
				duration: callDetails.duration,
			});
		}
	}, [callDetails]);

	return (
		<form
			onSubmit={e => {
				e.preventDefault();
				if (onSubmit) onSubmit({ ...formData });
			}}
			className="grid gap-[16px]"
		>
			{/* Call Type */}
			<div>
				<div className="mb-2 block">
					<Label htmlFor="title" value="Call type" className={`${labelStyle}`} />
					<span className="text-red-600 pl-1">*</span>
				</div>

				<fieldset
					className="flex flex-wrap max-w-md gap-4"
					onChange={e => {
						const target = e.target as HTMLInputElement;
						if (target?.value) {
							setFormData(prev => {
								return { ...prev, callType: target.value as BriedDebriefCallType };
							});
						}
					}}
				>
					{BriedDebriefCallType &&
						Object.values(BriedDebriefCallType).map((callType: string, i: number) => (
							<div key={i} className="flex items-center gap-2">
								<Radio
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={callType}
									name="callType"
									value={callType}
									checked={formData.callType === callType}
								/>
								<Label htmlFor={callType}>{camelCaseToSpace(callType)} Call</Label>
							</div>
						))}
				</fieldset>
			</div>
			{/* Call Label */}
			<div className="whiteBgSelect">
				<div className="mb-2 block">
					<Label htmlFor="tags" value="Call Label (eg: CEO Round, CTO Round etc.)" />
					<span className="text-red-600">*</span>
				</div>
				<Select
					menuPlacement={'auto'}
					classNamePrefix="my-select-plans"
					className="single-value-select-tag"
					closeMenuOnSelect={false}
					options={
						Object.values(RoundLabelType).map((label: RoundLabelType) => {
							return { label, value: label };
						}) as Dropdown[]
					}
					onChange={(val: SingleValue<Dropdown>) => {
						setFormData(prev => {
							return { ...prev, label: val?.value as RoundLabelType };
						});
					}}
					placeholder="Select Call Label"
					value={{
						label: formData?.label ?? '',
						value: formData?.label ?? '',
					}}
				/>
			</div>
			{/* Round Number  * */}
			<div className="mb-2 block">
				<div className="mb-[5px]">
					<Label htmlFor="roundNumber" value="Round Number" />
					<span className="text-[red]">*</span>
				</div>
				<TextInput
					disabled={!!roundNumber}
					name="roundNumber"
					type="number"
					value={formData.roundNumber}
					placeholder="eg: 1"
					className="w-full [&_input]:bg-white"
					onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
						setFormData({ ...formData, roundNumber: parseInt(e.target.value) })
					}
				/>
			</div>
			{/* Round Type  * */}
			<div className="mb-2 ">
				<div className="mb-2 block">
					<Label htmlFor="roundType" value="Round type" className={`${labelStyle}`} />
					<span className="text-red-600">*</span>
				</div>
				<fieldset
					className="flex flex-wrap max-w-md gap-4"
					onChange={e => {
						const target = e.target as HTMLInputElement;
						if (target?.value) {
							setFormData(prev => {
								return { ...prev, roundType: target.value as ApplicationRoundType };
							});
						}
					}}
				>
					{ApplicationRoundType &&
						Object.values(ApplicationRoundType).map((roundType: string, i: number) => (
							<div key={i} className="flex items-center gap-2">
								<Radio
									className="text-[#7777E7] focus:ring-[#7777E7]"
									id={roundType + i}
									name="roundType"
									value={roundType}
									checked={formData.roundType === roundType}
								/>
								<Label htmlFor={roundType + i}>{camelCaseToSpace(roundType)} Call</Label>
							</div>
						))}
				</fieldset>
			</div>

			{/* Prep Coach */}
			<div className="whiteBgSelect mb-2">
				<div className="mb-[8px]">
					<Label>{'Prep Coach'}</Label>
					<span className="text-[red]">*</span>{' '}
				</div>
				<Select
					classNamePrefix="my-select-plans"
					className="single-value-select-tag"
					isLoading={prepCoachesLoading}
					options={prepCoachesOptions}
					closeMenuOnSelect
					value={prepCoachesOptions.find(op => op.value === formData.prepCoach)}
					onChange={(val: SingleValue<Dropdown>) => {
						if (val) setFormData(prev => ({ ...prev, prepCoach: String(val.value) }));
					}}
				/>
			</div>

			{/* Additional guests */}
			<div className="mb-2 block whiteBgSelect">
				<div className="mb-[5px]">
					<Label className="font-inter text-[14px] font-[400] text-[#28293D]">
						{'Add additional guests emails if needed '}
					</Label>
					{/* <span className="text-[red]">* </span> */}
					<span className="text-[12px] text-yellow-600">(Calendar invites will be sent)</span>{' '}
				</div>
				<div className="text-[10px] mb-[4px]">(Enter email separated by comma or space after each email id.)</div>
				<CreatableSelect
					id="briefDebriefEmailSelect"
					components={{
						DropdownIndicator: null,
					}}
					inputValue={inputValue}
					isClearable
					isMulti
					menuIsOpen={false}
					onInputChange={newValue => {
						setInputValue(newValue);
						handleInputChange(newValue);
					}}
					onBlur={() => {
						if (inputValue) {
							handleInputChange(' ');
						}
					}}
					onChange={(vals: MultiValue<Dropdown>) => {
						setValue(vals);
						setFormData(prev => {
							return { ...prev, additionalEmails: vals.map(val => val.value) };
						});
					}}
					className="single-value-select-tag"
					classNamePrefix="my-select-plans"
					placeholder="teammate-name@company-name.com"
					value={value}
				/>

				{emailErrorMessage ? <div className="mt-2 text-sm text-red-500">{emailErrorMessage}</div> : null}
			</div>

			{/* Schedule Type */}
			<div>
				<div className="mb-2 block">
					<Label value="Type" className={`${labelStyle}`} />
					<span className="text-red-600 pl-1">*</span>
				</div>

				<div className="flex gap-4">
					<div className="flex items-center gap-2">
						<Radio
							id="request"
							className="text-[#7777E7] focus:ring-[#7777E7]"
							checked={scheduleType === 'request'}
							onChange={e => {
								if (e.target.checked) setScheduleType('request');
							}}
						/>
						<Label htmlFor="request">Request</Label>
					</div>
					<div className="flex items-center gap-2">
						<Radio
							id="schedule"
							className="text-[#7777E7] focus:ring-[#7777E7]"
							checked={scheduleType === 'schedule'}
							onChange={e => {
								if (e.target.checked) setScheduleType('schedule');
							}}
						/>
						<Label htmlFor="schedule">Schedule</Label>
					</div>
				</div>
			</div>

			{scheduleType === 'schedule' ? (
				<>
					{/* Meeting Time */}
					<div>
						<div>
							<div className="mb-[8px]">
								<Label>{'Call Time'}</Label>
								<span className="text-[red]">*</span>
							</div>
						</div>
						{/* Meeting Date */}
						<MobileDateTimePicker
							value={formData.meetingDateTime ? dayjs(formData.meetingDateTime) : null}
							minDateTime={dayjs()}
							format="DD MMM, YYYY, hh:mm A"
							onChange={date => {
								const dateValue = date as { $d?: Date };
								setFormData(prev => {
									return { ...prev, meetingDateTime: dateValue?.$d || new Date() };
								});
							}}
							sx={{
								'&': {
									width: '100%',
								},
								'& .MuiInputBase-input': {
									padding: '10px',
									borderRadius: '8px',
									backgroundColor: 'white',
									border: '1px solid rgb(209 213 219 / 1)',
									fontSize: '14px',
								},
								'& .MuiInputBase-root': {
									outline: 'none',
									padding: '0px',
									'&:hover': {
										outline: 'none',
									},
								},
								'& fieldset': {
									display: 'none',
								},
							}}
						/>
					</div>
					{/* Meeting link * */}
					<div className="mb-2 block">
						<div className="mb-[5px] flex items-end justify-between">
							<div>
								<Label
									className="font-inter text-[14px] font-[400] text-[#28293D]"
									htmlFor="interviewLink"
									value="Meeting link"
								/>
							</div>

							<div>
								<GenerateMeetingLinkButton
									positionId={positionId || ''}
									onGenerate={meetingLink => {
										setFormData({ ...formData, meetingLink });
									}}
								/>
							</div>
						</div>
						<TextInput
							name="meetingLink"
							type="meetingLink"
							value={formData.meetingLink}
							placeholder="Zoom / Google Meet/ Teams link"
							className="w-full [&_input]:bg-white"
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								setFormData({ ...formData, meetingLink: e.target.value })
							}
						/>

						{formData.meetingLink && !validateUrl(formData.meetingLink) ? (
							<div className="mt-2 text-sm text-red-500">Please enter a valid url</div>
						) : null}
					</div>
					{/* Meeting Duration (in minutes)  eg: type 120 to schedule a 2 hour call * */}
					<div className="mb-2 block">
						<div className="mb-[5px]">
							<Label
								className="font-inter text-[14px] font-[400] text-[#28293D]"
								htmlFor="duration"
								value="Meeting Duration  (in minutes)  eg: type 120 to schedule a 2 hour call"
							/>
							<span className="text-[red]">*</span>
						</div>
						<TextInput
							name="duration"
							type="number"
							value={formData.duration}
							placeholder="eg: 120"
							className="w-full [&_input]:bg-white"
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								setFormData({ ...formData, duration: parseInt(e.target.value) })
							}
						/>
					</div>
				</>
			) : null}

			{/* Meeting Description/Message */}
			<div>
				<div className="mb-[8px]">
					<Label>{'Message for Attendees'}</Label>
					<span className="text-[red]">*</span>{' '}
					<span className="italic text-xs text-[#6B7280] ">{(formData.message?.length || 0) + ' / ' + 640}</span>
				</div>
				<Textarea
					name="message"
					value={formData.message}
					maxLength={640}
					placeholder="Write text here ..."
					className="border bg-white dark:text-white dark:border-[#4B5563] min-h-[185px] dark:bg-[#374151] focus:border-transparent focus:!ring-[1px] focus:!ring-[#7777E7]"
					onChange={e => {
						setFormData(prev => {
							return { ...prev, message: e.target.value };
						});
					}}
				/>
			</div>

			<Button
				type="submit"
				className="w-full mt-[20px] bg-[#7777E7] sticky bottom-4"
				color="purplePlaced"
				disabled={isLoading || !isFormFilled}
			>
				{isLoading ? <Spinner /> : 'Schedule Call'}
			</Button>
		</form>
	);
};

export default BriefDebriefModal;
